import { Box, SelectChangeEvent } from '@mui/material';
import { GridCellParams, GridColDef, GridSortModel, MuiEvent } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useState } from 'react';
import { LiabilityEventDetails } from '../../@types';
import { LiabilityMonitoringEvent } from '../../@types/api';
import { REFETCH_INTERVAL_OPTIONS } from '../../constants';
import { defaultColumnsLiabilityMonitoring } from '../../helpers/table';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn } from '../../utils';
import IconRenderer from '../atoms/IconRenderer';
import SelectRefreshInterval from '../atoms/SelectRefreshInterval';
import TableIconContainer from '../atoms/TableIconContainer';
import LiabilityEvent from '../molecules/LiabilityEvent';
import TableTemplate from '../templates/TableTemplate';
import MobileDialogWrapper from '../organisms/MobileDialogWrapper';

const styles = {
  container: {
    display: 'flex',
    height: '100%',
    minHeight: 0,
    gap: '1.5rem',
    position: 'relative',
    width: '100%',
  },
};

const INITIAL_QUERY_PARAMS = {
  product: 'pre-match',
  page: 1,
  limit: 25,
  fromTimestamp: dayjs().startOf('day').valueOf(),
  toTimestamp: dayjs().startOf('day').add(3, 'day').valueOf(),
  sort: 'topLiability',
  order: 'DESC',
};

const SORT_FIELD_MAP: Record<string, string> = {
  event: 'eventName',
  numberTicketBets: 'betsCount',
};

const LiabilityMonitoringPage = () => {
  const [refetchInterval, setRefetchInterval] = useState<number | false>(0);
  const [selectedRow, setSelectedRow] = useState<LiabilityEventDetails | null>(null);

  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<LiabilityMonitoringEvent>(
    'risk-management/monitoring/events',
    INITIAL_QUERY_PARAMS,
    { refetchInterval }
  );

  const handleRefetchIntervalChange = (e: SelectChangeEvent<number | false>) => {
    setRefetchInterval(e.target.value as number | false);
  };

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const { handleSort } = useSort(changeQuery);

  const handleLiabilitySort = (model: GridSortModel) => {
    const sortModelItem = model[0];

    if (!sortModelItem) {
      return handleSort(model);
    }

    return handleSort([
      {
        ...sortModelItem,
        field: SORT_FIELD_MAP[sortModelItem.field] ?? sortModelItem.field,
      },
    ]);
  };

  const onCellClick = (params: GridCellParams, _event: MuiEvent<React.MouseEvent>) => {
    if (params.field !== 'edit' && params.field !== 'isActive' && params.field !== '__check__') {
      setSelectedRow({
        id: params.row.id,
        startDate: params.row.startDate,
        eventName: params.row.name,
        competitionName: params.row.tournament.competition.name,
        sportName: params.row.tournament.competition.sport.name,
        tournamentName: params.row.tournament.name,
      });
    }
  };

  const handleCloseSelectedRow = () => {
    setSelectedRow(null);
  };

  const conditionTypeColumns: GridColDef[] = [
    createColumn('id', 'Sport ID', {
      valueGetter: (_value, row, _column, _apiRef) => row.tournament.competition.sport.id,
    }),
    createColumn('icon', 'Icon', {
      renderCell: (params) => (
        <TableIconContainer>
          <IconRenderer name={params.row.tournament.competition.sport.icon} />
        </TableIconContainer>
      ),
    }),
    createColumn('sportName', 'Sport', {
      valueGetter: (_value, row, _column, _apiRef) => row.tournament.competition.sport.name,
    }),
    createColumn('competitionId', 'Competition ID', {
      valueGetter: (_value, row, _column, _apiRef) => row.tournament.competition.id,
    }),
    createColumn('competitionName', 'Competition', {
      valueGetter: (_value, row, _column, _apiRef) => row.tournament.competition.name,
    }),
    createColumn('tournamentId', 'Tournament ID', {
      valueGetter: (_value, row, _column, _apiRef) => row.tournament.id,
    }),
    createColumn('tournamentName', 'Tournament', {
      minWidth: 150,
      valueGetter: (_value, row, _column, _apiRef) => row.tournament.name,
    }),
    createColumn('uniqueId', 'Unique ID', {
      valueGetter: (_value, row, _column, _apiRef) => row.id + row.liability,
    }),
    createColumn('eventId', 'Event ID', {
      valueGetter: (_value, row, _column, _apiRef) => row.id,
    }),
    createColumn('event', 'Event', {
      minWidth: 250,
      valueGetter: (_value, row, _column, _apiRef) => row.name,
    }),
    createColumn('startDate', 'Start Date', {
      valueGetter: (_value, row, _column, _apiRef) => dayjs(row.startDate).format('DD.MM.YYYY'),
    }),
    createColumn('limitId', 'Limit ID', {
      valueGetter: (_value, row, _column, _apiRef) => row.id,
    }),
    createColumn('limitValue', 'Limit Value', {
      valueGetter: (value, row, _column, _apiRef) => row.liability?.value,
    }),
    createColumn('topLiability', 'Top Liability', {
      valueGetter: (_value, row, _column, _apiRef) => row.liabilityMax?.toFixed(2),
    }),
    createColumn('liabilityPercentage', 'Liability Percentage', {
      valueGetter: (value, row, _column, _apiRef) => {
        if (row.liabilityTotal && row.liability) {
          return (row.liabilityTotal ? (Number(row?.liabilityMax) / row.liability.value) * 100 : 0).toFixed(2) + '%';
        }
      },
      sortable: false,
    }),
    createColumn('numberTicketBets', 'No. Ticket Bets', {
      valueGetter: (_value, row, _column, _apiRef) => row.betsCount,
    }),
    createColumn('riskFactor', 'Risk Factor', {
      valueGetter: (_value, row, _column, _apiRef) => row.riskFactor,
    }),
  ];

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={data?.items || []}
        rowCount={data?.count || 0}
        columns={conditionTypeColumns}
        defaultVisibleColumns={defaultColumnsLiabilityMonitoring}
        loading={isFetching || isLoading}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleLiabilitySort}
        changeQuery={changeQuery}
        onCellClick={onCellClick}
        customTableHeaderContent={
          <SelectRefreshInterval
            handleRefetchIntervalChange={handleRefetchIntervalChange}
            options={REFETCH_INTERVAL_OPTIONS}
            value={refetchInterval}
          />
        }
      />
      {selectedRow && (
        <MobileDialogWrapper>
          <LiabilityEvent eventDetails={selectedRow} onClose={handleCloseSelectedRow} />
        </MobileDialogWrapper>
      )}
    </Box>
  );
};

export default LiabilityMonitoringPage;
